/**
 * /well-testing/jobs/:id — one job (GET / PATCH / DELETE
 * /well-testing/jobs/:id, fieldiq-api#1245, WT-2).
 *
 * A full page with tabs (`?tab=`, so Back and a shared link keep the place):
 * Overview (details, edit), Crew, Equipment (owned units and packages),
 * Rent-ins and Shift logs; WT-3 adds Readings (with test periods), Chart,
 * Flags, Daily volumes and Reports (ReadingSections.tsx, ReportsSection.tsx).
 * WT-6's AI launchers sit on Shift logs (shift summary) and Tickets (job
 * preflight), for the AI review roles only; each draft shows in place.
 * Forms open inline, never as a modal; a status move,
 * a delete and a removal ask in-app first.
 *
 * THE STATUS BAR SHOWS ONLY THE MOVES THE API ALLOWS. The buttons are the job's
 * own `allowedTransitions` (the API's WT_JOB_TRANSITIONS from where it stands),
 * shown to the dispatch roles only — nothing here keeps a second copy of the
 * state machine. Cancelling asks for a reason (the API requires one). A move
 * the API still refuses (another session moved it first) reads as the
 * invalid_transition sentence naming where it can go.
 *
 * Delete is offered on a draft or scheduled job; a job with logs is refused
 * (job_has_logs) and the refusal says to cancel instead. Rent-in cost is shown
 * only to the roles the API sends it to.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, ClipboardList, Pencil, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  useDeleteWtJob,
  useUpdateWtJob,
  useWtEquipmentOptions,
  useWtJob,
  useWtMeta,
  useWtUsers,
} from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canDispatchWt,
  canSeeWtRentInCost,
  canWorkWt,
  catalogLabel,
  wtDay,
  wtJobDeletable,
  wtJobLoggable,
  wtJobOpen,
  wtJobStatusSkin,
  wtQuantity,
  wtRefusal,
} from '../../lib/well-testing';
import { useBackToList, useOpenRecord } from '../downhole/recordPage';
import { plannedRange } from './JobBits';
import { JobForm } from './JobForm';
import { CrewSection, EquipmentSection, LogsSection, RentInsSection } from './JobSections';
import { ChartSection, FlagsSection, ReadingsSection, VolumesSection } from './ReadingSections';
import { ReportsSection } from './ReportsSection';
import { WtJobTicketsSection, WtRentInBillingPanel } from './tickets/JobTicketSections';
import { WtJobInvoicesSection } from './invoices/InvoiceBits';
import { canReadWtTickets, canWriteWtTickets } from '../../lib/wt-tickets';
import { canUseWtAi } from '../../lib/wt-ai';
import { WtJobPreflight, WtJobShiftSummary } from './ai/WtAiBits';
import {
  isNotFound,
  WT_BAND_SECONDARY,
  WT_EYEBROW,
  WT_INPUT,
  WT_PRIMARY,
  WT_SECONDARY,
  WT_SOUR_SKIN,
  WtBadge,
  WtDialog,
  WtEmpty,
  WtField,
  WtFormError,
  WtLoading,
  WtPanel,
  WtSpec,
} from './WtBits';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'crew', label: 'Crew' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'rent-ins', label: 'Rent-ins' },
  { key: 'logs', label: 'Shift logs' },
  // WT-3 (fieldiq-api#1248)
  { key: 'readings', label: 'Readings' },
  { key: 'chart', label: 'Chart' },
  { key: 'flags', label: 'Flags' },
  { key: 'volumes', label: 'Daily volumes' },
  { key: 'reports', label: 'Reports' },
  // WT-4 (fieldiq-api#1253) — the office, supervisor and dispatch only (prices)
  { key: 'tickets', label: 'Tickets' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** The button for a move, by where it goes. */
export const MOVE_LABEL: Record<string, string> = {
  draft: 'Back to draft',
  scheduled: 'Schedule',
  rigging_up: 'Start rig-up',
  flowing: 'Start flowing',
  rigging_down: 'Start rig-down',
  completed: 'Complete',
  cancelled: 'Cancel job',
};
const MOVE_BODY: Record<string, string> = {
  draft: 'Moves the job back to draft. The crew cannot log a shift on a draft.',
  scheduled: 'Schedules the job. The crew can log shifts on it from today.',
  rigging_up: 'Marks the crew rigging up on location. The first shift log does this on its own.',
  flowing: 'Marks the well flowing through the package.',
  rigging_down: 'Marks the crew rigging down.',
  completed: 'Completes the job. A completed job takes no more logs or changes.',
  cancelled: 'Cancels the job. A cancelled job takes no more logs or changes.',
};

export default function WtJobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const back = useBackToList('/well-testing/jobs');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const tab: TabKey = TABS.some((t) => t.key === tabParam) ? (tabParam as TabKey) : 'overview';
  const pickTab = (key: TabKey) => {
    const p = new URLSearchParams(searchParams);
    if (key === 'overview') p.delete('tab');
    else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };

  const { user } = useAuth();
  const role = user?.role;
  const canDispatch = canDispatchWt(role);
  const query = useWtJob(id);
  const catalog = useWtMeta().data?.data ?? null;
  const users = useWtUsers(canDispatch);
  const equipment = useWtEquipmentOptions(canDispatch);
  const update = useUpdateWtJob();
  const del = useDeleteWtJob();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [move, setMove] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const job = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const open = !!job && wtJobOpen(job.status);
  const canWrite = canDispatch && open;
  const moves = canDispatch && job ? job.allowedTransitions ?? [] : [];
  const forward = moves.find((m) => m !== 'cancelled' && m !== 'draft');

  const doMove = () => {
    if (!job || !move) return;
    setMoveError(null);
    if (move === 'cancelled' && !cancelReason.trim()) return setMoveError('Say why the job is cancelled.');
    update.mutate(
      { id: job.id, body: move === 'cancelled' ? { status: move, cancel_reason: cancelReason.trim() } : { status: move } },
      {
        onSuccess: () => {
          toast.success(`Job ${wtJobStatusSkin(move).label.toLowerCase()}`);
          setMove(null);
          setCancelReason('');
        },
        onError: (err) => setMoveError(wtRefusal(err, 'The status could not be changed.')),
      },
    );
  };

  const counts: Partial<Record<TabKey, number>> = job ? { crew: job.crew.length, equipment: job.equipment.length, 'rent-ins': job.rentIns.length } : {};

  return (
    <div data-testid="wt-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={job?.jobKindLabel ? `${WT_EYEBROW} · ${job.jobKindLabel}` : WT_EYEBROW}
        title={job?.jobNumber || (query.isLoading ? 'Job' : '—')}
        description={job ? `${job.customerName ?? '—'} · ${job.wellName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          job && canDispatch ? (
            <div className="flex flex-wrap gap-2" data-testid="wt-job-actions">
              {moves.map((to) => (
                <button
                  key={to}
                  type="button"
                  data-move={to}
                  data-testid={`wt-move-${to}`}
                  className={to === forward ? 'fi-action fi-action--standard fi-action--primary' : WT_BAND_SECONDARY}
                  onClick={() => { setMoveError(null); setCancelReason(''); setMove(to); }}
                >
                  {MOVE_LABEL[to] ?? wtJobStatusSkin(to).label}
                </button>
              ))}
              {open && (
                <button type="button" className={WT_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); pickTab('overview'); }} disabled={editing}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </button>
              )}
              {wtJobDeletable(job.status) && (
                <button type="button" className={WT_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: job ? wtJobStatusSkin(job.status).label : '—' },
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Crew', tone: 'neutral', loading: query.isLoading, value: job ? displayValue(job.crew.length) : '—' },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Shift logs', sub: job?.lastLogDate ? `Last ${wtDay(job.lastLogDate)}` : undefined, tone: 'neutral', loading: query.isLoading, value: job ? displayValue(job.shiftLogCount ?? undefined) : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WtLoading label="Loading job" />
          ) : notFound || (!failure && !job) ? (
            <WtPanel eyebrow="Job" title="Job">
              <WtEmpty>This job was not found. It may have been deleted, or you may not be on its crew.</WtEmpty>
            </WtPanel>
          ) : failure || !job ? (
            <WtPanel eyebrow="Job" title="Job">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this job" />
            </WtPanel>
          ) : (
            <>
              <div role="tablist" aria-label="Job sections" className="flex gap-5 overflow-x-auto border-b border-fieldiq-border">
                {TABS.filter((t) => t.key !== 'tickets' || canReadWtTickets(role)).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => pickTab(t.key)}
                    className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm transition ${
                      tab === t.key ? 'border-fi-amber font-extrabold text-fieldiq-text' : 'border-transparent font-bold text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {t.label}
                    {counts[t.key] !== undefined ? ` (${counts[t.key]})` : ''}
                  </button>
                ))}
              </div>

              {tab === 'overview' && (
                <>
                  {!open && (
                    <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="wt-job-closed">
                      This job is {wtJobStatusSkin(job.status).label.toLowerCase()}. It takes no more logs or changes.
                      {job.cancelReason ? ` Reason: ${job.cancelReason}` : ''}
                    </p>
                  )}
                  <WtPanel eyebrow={job.jobKindLabel ?? 'Job'} title="Details" testId="wt-job-details">
                    <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                      <WtSpec label="Status" value={<WtBadge skin={wtJobStatusSkin(job.status)} value={job.status} />} />
                      <WtSpec label="Customer" value={job.customerName || '—'} />
                      <WtSpec
                        label="Well"
                        value={
                          <button type="button" className="text-left font-bold text-fieldiq-text underline" onClick={() => openRecord(`/well-testing/wells/${job.wellId}`)}>
                            {job.wellName || '—'}
                          </button>
                        }
                      />
                      <WtSpec label="UWI / API number" value={job.wellUwi || job.wellApiNumber || '—'} mono />
                      <WtSpec label="Service" value={job.wellSour ? <WtBadge skin={WT_SOUR_SKIN} value="sour" /> : job.wellSour === false ? 'Sweet' : '—'} />
                      <WtSpec label="Tickets" value={catalogLabel(catalog?.ticketModes, job.ticketMode)} />
                      <WtSpec label="Supervisor" value={job.supervisorName || '—'} />
                      <WtSpec label="Planned" value={plannedRange(job.plannedStartOn, job.plannedEndOn)} />
                      <WtSpec label="Load to recover" value={wtQuantity(job.loadToRecover)} mono />
                      <WtSpec label="Flare permit" value={job.flarePermitRef || '—'} mono />
                      <WtSpec label="PO number" value={job.poNumber || '—'} mono />
                      <WtSpec label="AFE number" value={job.afeNumber || '—'} mono />
                      <WtSpec label="Cost code" value={job.costCode || '—'} mono />
                      <WtSpec label="Currency" value={job.currency || '—'} />
                      <WtSpec label="Owned units" value={displayValue(job.equipment.length)} />
                      <WtSpec label="Rent-ins" value={displayValue(job.rentIns.length)} />
                      {/* Only a job booked from a quote has one (api#1258); a payload without the key draws nothing. */}
                      {job.sourceQuote?.id && (
                        <WtSpec
                          label="Quote"
                          value={
                            <Link to={`/quotes/${job.sourceQuote.id}`} data-testid="wt-job-source-quote" className="font-bold text-fieldiq-text underline">
                              From quote {job.sourceQuote.number || '—'}
                            </Link>
                          }
                        />
                      )}
                    </dl>
                    {job.scope && <p data-testid="wt-job-scope" className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Scope: </span>{job.scope}</p>}
                    {job.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{job.notes}</p>}
                  </WtPanel>

                  {editing && canWrite && (
                    <WtPanel eyebrow="Edit" title="Edit job" testId="wt-job-edit">
                      <JobForm
                        job={job}
                        catalog={catalog}
                        meta={meta}
                        users={users.data ?? []}
                        pending={update.isPending}
                        error={editError}
                        submitLabel="Save job"
                        onCancel={() => setEditing(false)}
                        onSubmit={(body) =>
                          update.mutate(
                            { id: job.id, body },
                            {
                              onSuccess: () => {
                                toast.success('Job saved');
                                setEditing(false);
                              },
                              onError: (err) => setEditError(wtRefusal(err, 'The job could not be saved.')),
                            },
                          )
                        }
                      />
                    </WtPanel>
                  )}
                </>
              )}

              {tab === 'crew' && <CrewSection job={job} catalog={catalog} users={users.data ?? []} canWrite={canWrite} />}
              {tab === 'equipment' && <EquipmentSection job={job} equipment={equipment.data ?? []} canWrite={canWrite} />}
              {tab === 'rent-ins' && (
                <RentInsSection job={job} catalog={catalog} currency={job.currency ?? meta?.currency ?? null} canWrite={canWrite} canSeeCost={canSeeWtRentInCost(role)} />
              )}
              {tab === 'logs' && (
                <div className="space-y-4">
                  <LogsSection
                    job={job}
                    canLog={canWorkWt(role) && wtJobLoggable(job.status)}
                    onNew={() => openRecord(`/well-testing/jobs/${job.id}/shift-logs/new`)}
                    onOpen={(l) => openRecord(`/well-testing/shift-logs/${l.id}`)}
                  />
                  {/* WT-6: the AI review roles only (WT_AI_ROLES); the draft shows in place, awaiting approval. */}
                  {canUseWtAi(role) && <WtJobShiftSummary jobId={job.id} today={meta?.today ?? null} canReview />}
                </div>
              )}
              {tab === 'readings' && (
                <ReadingsSection
                  job={job}
                  meta={meta}
                  catalog={catalog}
                  canWork={canWorkWt(role)}
                  canDispatch={canDispatch}
                  loggable={wtJobLoggable(job.status)}
                />
              )}
              {tab === 'chart' && <ChartSection job={job} meta={meta} />}
              {tab === 'flags' && <FlagsSection job={job} meta={meta} canDispatch={canDispatch} />}
              {tab === 'volumes' && <VolumesSection job={job} meta={meta} canDispatch={canDispatch} />}
              {tab === 'reports' && <ReportsSection job={job} meta={meta} canDispatch={canDispatch} />}
              {tab === 'tickets' && canReadWtTickets(role) && (
                <div className="space-y-4">
                  <WtJobTicketsSection job={job} canWrite={canWriteWtTickets(role)} today={meta?.today ?? null} />
                  {canUseWtAi(role) && <WtJobPreflight jobId={job.id} canReview />}
                  <WtRentInBillingPanel job={job} rentIns={job.rentIns ?? []} canWrite={canWriteWtTickets(role)} />
                  <WtJobInvoicesSection jobId={job.id} />
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {job && canDispatch && (
        <>
          <WtDialog
            open={!!move}
            title={move ? MOVE_LABEL[move] ?? 'Change status' : 'Change status'}
            onClose={() => setMove(null)}
            footer={
              <>
                <button type="button" className={WT_SECONDARY} onClick={() => setMove(null)} disabled={update.isPending}>
                  Not now
                </button>
                <button type="button" className={WT_PRIMARY} onClick={doMove} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : move ? MOVE_LABEL[move] ?? 'Confirm' : 'Confirm'}
                </button>
              </>
            }
          >
            <WtFormError message={moveError} />
            <p className="text-sm text-fieldiq-text">{move ? MOVE_BODY[move] ?? '' : ''}</p>
            {move === 'cancelled' && (
              <div className="mt-3">
                <WtField label="Reason">
                  <textarea className={`${WT_INPUT} h-20 py-2`} aria-label="Cancel reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </WtField>
              </div>
            )}
          </WtDialog>
          <WtDialog
            open={deleting}
            title="Delete job"
            onClose={() => setDeleting(false)}
            footer={
              <>
                <button type="button" className={WT_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                  Keep job
                </button>
                <button
                  type="button"
                  className={WT_PRIMARY}
                  disabled={del.isPending}
                  onClick={() =>
                    del.mutate(job.id, {
                      onSuccess: () => {
                        toast.success('Job deleted');
                        navigate('/well-testing/jobs', { replace: true });
                      },
                      onError: (err) => setDeleteError(wtRefusal(err, 'The job could not be deleted.')),
                    })
                  }
                >
                  {del.isPending ? 'Deleting…' : 'Delete job'}
                </button>
              </>
            }
          >
            <WtFormError message={deleteError} />
            <p className="text-sm text-fieldiq-text">Removes {job.jobNumber} from the register. A job with shift logs cannot be deleted — cancel it instead.</p>
          </WtDialog>
        </>
      )}
    </div>
  );
}
